// swipeGesture.ts — Entscheidungslogik der Wisch-Geste, bewusst DOM-frei.
//
// Die eigentliche Schwierigkeit einer Wisch-Geste in einer scrollenden Liste
// sind nicht die Pointer-Events, sondern zwei Konflikte:
//
//   1. Die Mailliste scrollt vertikal (`overflowY: auto`). Ein zu gieriger
//      Handler macht sie unbenutzbar.
//   2. Die Browser haben am Bildschirmrand eigene Gesten (Zurück/Vorwärts).
//
// Beides wird hier entschieden — als reine Funktionen, damit es ohne Gerät und
// ohne jsdom-Pointer-Emulation testbar bleibt.

/** Ab dieser Bewegung (px) fällt die Entscheidung, welche Achse gemeint ist. */
export const AXIS_LOCK_PX = 10;

/** Zone am linken/rechten Rand (px), in der die System-Geste Vorrang hat. */
export const EDGE_GUARD_PX = 20;

/** Anteil der Zeilenbreite, ab dem die Aktion beim Loslassen auslöst. */
export const COMMIT_RATIO = 0.4;

/** Wischgeschwindigkeit (px/ms), ab der auch ein kurzer Wisch auslöst. */
export const FLICK_VELOCITY = 0.5;

export type Axis = "undecided" | "horizontal" | "vertical";

/**
 * Achsen-Entscheidung. Solange die Bewegung unter `AXIS_LOCK_PX` liegt, bleibt
 * sie offen — erst danach steht fest, ob gewischt oder gescrollt wird.
 *
 * Bei `"vertical"` gibt die Geste auf und rührt das Scrollen NICHT an; nur bei
 * `"horizontal"` übernimmt sie. Diagonale Bewegungen zählen als vertikal, damit
 * im Zweifel das Scrollen gewinnt — ein versehentlich blockierter Scroll ist
 * ärgerlicher als eine nicht erkannte Wischgeste.
 */
export function decideAxis(dx: number, dy: number): Axis {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    if (ax < AXIS_LOCK_PX && ay < AXIS_LOCK_PX) return "undecided";
    return ax > ay ? "horizontal" : "vertical";
}

/**
 * Darf an dieser Startposition überhaupt gewischt werden?
 *
 * iOS Safari und Android Chrome legen auf die Bildschirmränder die
 * Zurück-/Vorwärts-Navigation. Gesten, die dort beginnen, gehören dem System —
 * CSS kann das nicht abschalten, also fassen wir sie gar nicht erst an.
 */
export function isEdgeStart(clientX: number, viewportWidth: number): boolean {
    return clientX <= EDGE_GUARD_PX || clientX >= viewportWidth - EDGE_GUARD_PX;
}

/**
 * Löst die Aktion beim Loslassen aus?
 *
 * Zwei Wege: weit genug gewischt (Anteil der Zeilenbreite) ODER schnell genug
 * geschnippt. Ohne den zweiten Weg müsste man auf einem breiten Tablet
 * unangenehm weit ziehen.
 *
 * `dx` ist negativ (Wisch nach links). Ein Wisch nach rechts löst nie aus — die
 * Richtung ist bewusst die von iOS Mail und Outlook Mobile.
 */
export function shouldCommit(dx: number, width: number, elapsedMs: number): boolean {
    if (dx >= 0 || width <= 0) return false;
    const distance = Math.abs(dx);
    if (distance >= width * COMMIT_RATIO) return true;
    // Sehr kurze Gesten (< 1ms) würden sonst durch die Division ausschlagen.
    const velocity = elapsedMs > 0 ? distance / elapsedMs : 0;
    return velocity >= FLICK_VELOCITY && distance >= AXIS_LOCK_PX * 3;
}

/**
 * Sichtbarer Versatz der Zeile. Nach links frei beweglich (bis Zeilenbreite),
 * nach rechts gesperrt: es liegt dort keine Aktion, und ein mitziehender Rand
 * würde eine Funktion suggerieren, die es nicht gibt.
 */
export function clampOffset(dx: number, width: number): number {
    if (dx >= 0) return 0;
    return Math.max(dx, -width);
}
